/* eslint-disable react/no-unescaped-entities */
import * as yup from 'yup'
import { toast } from 'react-toastify'
import { Info } from '@mui/icons-material'
import { yupResolver } from '@hookform/resolvers/yup'
import { FormProvider, useForm } from 'react-hook-form'
import { Box, CircularProgress, Divider, Typography, useMediaQuery } from '@mui/material'
import { Dialog } from '~/components/Dialog'
import { Form } from '~/components/Form/Form'
import { Input } from '~/components/Form/Input'
import { Button } from '~/components/Form/Button'
import { EInvoiceCancelReason, EInvoiceStatus, InvoiceListQuery, useProviderInvoiceCancelMutation } from '~/graphql/types'
import { formatDate, formatHour, theme, Unarray } from '~/utils'
import { Select } from '~/components/Form/Select'
import { invoiceCanceledReasonOptions } from '~/utils/options'
import { useCallback, useEffect, useState } from 'react'
import { IconCircleCheckFilled } from '@tabler/icons-react'

type InvoiceCancelForm = {
  description: string
  reason: EInvoiceCancelReason
  invoiceId: string
}

const schema = yup.object().shape({
  description: yup.string().required('Campo obrigatório'),
  reason: yup.string().required('Campo obrigatório')
})

export type InvoiceCancelDialogProps = {
  isVisible: boolean
  setIsVisibleInvoiceCancelModal: React.Dispatch<React.SetStateAction<boolean>>
  invoiceId: string
  onInvoiceCancelSuccess: () => void
  selectedInvoice: Unarray<InvoiceListQuery['invoiceList']['data']> | null
}
export const InvoiceCancelDialog: React.FC<InvoiceCancelDialogProps> = ({ isVisible, setIsVisibleInvoiceCancelModal, invoiceId, onInvoiceCancelSuccess, selectedInvoice }) => {
  const [cancelSuccess, setCancelSuccess] = useState(false)
  const [cancelInvoice, { loading }] = useProviderInvoiceCancelMutation()
  const isLowerSm = useMediaQuery(theme.breakpoints.down('sm'))
  const methods = useForm<InvoiceCancelForm>({
    resolver: yupResolver(schema),
    defaultValues: {
      reason: '' as EInvoiceCancelReason,
      description: ''
    }
  })

  const onCloseModal = useCallback(() => {
    setIsVisibleInvoiceCancelModal(false)
    setCancelSuccess(false)
  }, [])

  const onSubmit = async (formData: InvoiceCancelForm) => {
    if (!invoiceId) {
      return toast.error('Houve um problema ao cancelar a fatura', {
        autoClose: 3000,
        position: 'top-right'
      })
    }

    try {
      await cancelInvoice({
        variables: {
          params: {
            invoiceId,
            reason: formData.reason,
            description: formData.description
          }
        }
      })
      setCancelSuccess(true)
      toast.success('Fatura cancelada com sucesso!', {
        autoClose: 3000,
        position: 'top-right'
      })
      onInvoiceCancelSuccess()
    } catch (err) {
      toast.error('Houve um problema ao cancelar a fatura', {
        autoClose: 3000,
        position: 'top-right'
      })
    }
  }

  const isInvoiceCanceled = selectedInvoice?.status === EInvoiceStatus.canceled

  useEffect(() => {
    if (invoiceId && isInvoiceCanceled) {
      methods.setValue('reason', selectedInvoice.cancelInfo?.reason as EInvoiceCancelReason)
      methods.setValue('description', selectedInvoice.cancelInfo?.description || '')
    } else {
      methods.reset()
    }
  }, [selectedInvoice])

  return (

    cancelSuccess ? (
      <Dialog
        isVisible={isVisible}

        onClose={onCloseModal}
        minWidth={700}

      >

        <Box sx={{ textAlign: 'center', padding: '2rem' }}>
          <Box color={'#34C759'}>
            <IconCircleCheckFilled size={100} />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant='h5' fontWeight={600}>
              Sucesso!
            </Typography>
            <Typography variant='body1' fontWeight={400} color={'#999'}>
              Fatura cancelada
            </Typography>
          </Box>
          <Button
            color='primary'
            onClick={onCloseModal}
            sx={{ marginTop: '2rem', fontSize: '16px' }}
          >
            Entendi
          </Button>
        </Box>
      </Dialog>
    ) : (
      <Dialog
        isVisible={isVisible}
        title={!selectedInvoice?.cancelInfo ? 'Cancelar fatura' : 'Fatura cancelada'}
        onClose={onCloseModal}
        minWidth={700}
        titleIcon={<Info fontSize='large' color='error' />}
      >
        <FormProvider {...methods}>
          <Form onSubmit={methods.handleSubmit(onSubmit)}>
            <Box sx={{ flexDirection: 'column', display: 'flex', gap: 3 }}>
              <Divider />
              {!isInvoiceCanceled && (
                <Typography>
                  O cancelamento desta fatura é definitivo. Para prosseguir, selecione um motivo, descreva em poucas palavras o porquê do cancelamento e clique em "cancelar fatura".
                </Typography>
              )}

              <Select label='Motivo' name='reason' options={invoiceCanceledReasonOptions} disabled={Boolean(isInvoiceCanceled)} />
              <Input minRows={10} multiline name='description' placeholder='Descreva melhor o motivo do cancelamento da proposta' disabled={Boolean(isInvoiceCanceled)} />
              {isInvoiceCanceled && (
                <Typography variant='body3' color={'#999'}>
                  Cancelamento sinalizado em: <span style={{ fontWeight: 600 }}>{formatDate(selectedInvoice.cancelInfo?.createdAt)}</span> às <span style={{ fontWeight: 600 }}>{formatHour(selectedInvoice.cancelInfo?.createdAt)}</span> | por: <span style={{ fontWeight: 600 }}>{selectedInvoice.cancelInfo?.canceledBy}</span>
                </Typography>
              )}
              <Divider />
              {!isInvoiceCanceled ? (
                <Box sx={{ flexDirection: 'row', gap: 3, display: 'flex', justifyContent: 'space-between', flexFlow: isLowerSm ? 'column' : 'row' }}>

                  <Button
                    color='secondary'
                    disabled={loading}
                    onClick={onCloseModal}
                  >
                    Voltar
                  </Button>
                  <Button
                    type='submit'
                    color='error'
                    disabled={loading}
                    endIcon={loading ? <CircularProgress size={24} color='inherit' /> : null}
                  >
                    Cancelar fatura
                  </Button>
                </Box>
              ) : (
                <Box sx={{ flexDirection: 'row', gap: 3, display: 'flex', alignSelf: 'end', flexFlow: isLowerSm ? 'column' : 'row' }}>
                  <Button
                    color='error'
                    disabled={loading}
                    onClick={onCloseModal}
                  >
                    Entendi
                  </Button>

                </Box>
              )}
            </Box>
          </Form>
        </FormProvider>

      </Dialog >
    )
  )
}
